import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Submission } from '../../model/paper';
import { SubmissionsService } from '../../service/submissions.service';
import { SubmissionStatus } from '../../enum/submission.status';
import { NavbarService } from 'src/app/service/navbar.service';
import { NO_PAGINATION } from 'src/app/model/pagination.options';
import { ShowAllPagedViewDirective } from 'src/app/class/paged-view-all';
import { PreferencesService } from 'src/app/service/preferences.service';
import { LocalCache } from 'src/app/enum/local.cache';
import { Event } from 'src/app/model/event';
import { DefaultFilterPipe } from 'src/app/pipe/default-filter.pipe';
import { CountryPipe } from 'src/app/pipe/country.pipe';
import { SelectionClipboardService } from 'src/app/service/selection-clipboard.service';
import { NotificationService } from 'src/app/service/notification.service';

class SubmissionsByEvent {
  event: Event;
  count: number;
  submissions: Array<Submission>;
}

@Component({
  selector: 'app-submissions',
  templateUrl: './submissions.component.html',
  styleUrls: ['./submissions.component.scss']
})
export class SubmissionsComponent extends ShowAllPagedViewDirective implements AfterViewInit {
  public filterNotification: boolean = this.checkedFilterNotification;
  public submissionsByEvent: any[] = null;
  public search: string;

  // Sticky header — flag de scroll usada pra ativar `.is-scrolled`.
  public isScrolled = false;

  // Cards começam invisíveis (`.cards-pending` em main-body) até a
  // equalização das colunas rodar — evita o "dança" entre o primeiro paint
  // (cards com largura natural) e o final (todos alinhados).
  public cardsReady = false;

  @HostListener('window:resize')
  onWindowResizeForCols(): void {
    this.equalizeCardColumns();
  }

  // Iguala largura das colunas 1 (ID) e 3 (arquivos) em todos os cards
  // novos da página. Mesma lógica do equalize em Minhas revisões.
  private equalizeCardColumns(): void {
    const host: HTMLElement = this.elementRef.nativeElement;
    const ids = Array.from(host.querySelectorAll('.snc-id-col')) as HTMLElement[];
    const asides = Array.from(host.querySelectorAll('.snc-files-aside')) as HTMLElement[];
    if (!ids.length && !asides.length) { return; }

    // Idempotente: se todos os cols de um sel já têm o mesmo width, pula
    // — sem reset+set (evita loop com ResizeObserver).
    const applyMax = (els: HTMLElement[]) => {
      if (els.length < 2) { return; }
      const widths = els.map(e => e.offsetWidth);
      const max = Math.max(...widths);
      if (widths.every(w => w === max)) { return; }
      const px = `${max}px`;
      els.forEach(el => { el.style.minWidth = px; });
    };
    applyMax(ids);
    applyMax(asides);
    this.cardsReady = true;
  }

  // trackBy do *ngFor — estabiliza identidade dos cards mesmo quando o
  // defaultFilter pipe retorna uma array nova a cada CD (preserva min-width).
  public trackSubmissionById(_index: number, submission: any): any {
    return submission?.id;
  }

  // Caches por evento — chamadas por CD tick do template. WeakMap
  // libera automaticamente quando o grupo some.
  private _eventUrlCache = new WeakMap<any, string>();
  private _eventLocationCache = new WeakMap<any, string>();

  public formatEventUrl(event: any): string {
    if (!event) { return ''; }
    const hit = this._eventUrlCache.get(event);
    if (hit !== undefined) { return hit; }
    const v = event.uri ? `${window.location.origin}/${event.uri}` : '';
    this._eventUrlCache.set(event, v);
    return v;
  }

  public formatEventLocation(event: any): string {
    if (!event) { return ''; }
    const hit = this._eventLocationCache.get(event);
    if (hit !== undefined) { return hit; }
    const info = event?.eventInfo;
    let v = '';
    if (info) {
      const cityState = [info.city, info.state].filter(vv => !!vv).join(' - ');
      const country = info.country ? this.countryPipe.transform(info.country) : '';
      v = [cityState, country].filter(vv => !!vv).join(', ');
    }
    this._eventLocationCache.set(event, v);
    return v;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.isScrolled = window.scrollY > 4;
  }

  // Chave de localStorage onde os status selecionados são persistidos.
  private static readonly FILTER_STORAGE_KEY = 'jems3.submissions.filter.status';

  // Ordem fixa dos chips de status no menu de filtro (flex order via JS).
  private static readonly STATUS_ORDER: Record<string, number> = {
    pending: 0,
    active: 1,
    accepted: 2,
    rejected: 3,
    withdrawn: 4,
  };

  // Seleção pra copy-to-clipboard. Set<id> mantido aqui; cada card recebe
  // [selected]/[selectable] e emite (selectionChange) com novo estado.
  public selectedIds = new Set<number>();

  constructor(
    private submissionService: SubmissionsService,
    public navbarService: NavbarService,
    public preferencesService: PreferencesService,
    public defaultFilter: DefaultFilterPipe,
    public countryPipe: CountryPipe,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private selectionClipboard: SelectionClipboardService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
  ) {
    super(preferencesService, LocalCache.OLD_SUBMISSIONS);
  }

  // Preset ativo: view efêmera (?preset=...) que NÃO sobrescreve os
  // filtros salvos do usuário. Encerra na 1ª interação com chip ou
  // com o toggle "incluir submissões antigas".
  public _presetActive = false;
  public _presetStatus: string[] = [];
  private _presetInitConsumed = false;

  // Presets suportados. Novo preset = nova entrada + link no badge.
  private static readonly PRESETS: Record<string, { status: string[], showAll: boolean }> = {
    'drafts': { status: ['PENDING'], showAll: false },
  };

  // Override do ngOnInit do ShowAllPagedViewDirective — interceptamos
  // antes do getPreference/load pra plugar o preset (se houver
  // `?preset=...` no queryParams).
  ngOnInit(): void {
    setTimeout(() => {
      const preset = this.route.snapshot.queryParamMap.get('preset');
      const spec = preset ? SubmissionsComponent.PRESETS[preset] : null;
      if (spec) {
        this._presetActive = true;
        this._presetStatus = spec.status.slice();
        this.filter.status = spec.status.slice() as SubmissionStatus[];
        this.toggleNgModel = spec.showAll;
      } else {
        this.toggleNgModel = <boolean>this.preferencesService.getPreference(this.showAllPreferenceKey);
      }
      this.toggle?.change.subscribe((change: MatCheckboxChange) => {
        this._presetActive = false;
        this.preferencesService.changePreference(this.showAllPreferenceKey, change.checked);
      });
      this.load();
    });
  }

  // Override do updateToggle do parent — encerra o preset (é ação
  // explícita do usuário sobre "incluir submissões antigas").
  updateToggle(): void {
    this._presetActive = false;
    super.updateToggle();
  }

  public toggleSelection(id: number, checked: boolean): void {
    if (checked) { this.selectedIds.add(id); } else { this.selectedIds.delete(id); }
  }

  public clearSelection(): void {
    this.selectedIds.clear();
  }

  public async copySelection(): Promise<void> {
    const flat: Submission[] = (this.submissionsByEvent || [])
      .flatMap((g: any) => g?.submissions || []);
    const items = flat.filter(s => this.selectedIds.has(s.id));
    // Notificação removida pra evitar o "pulo" da página (MatSnackBar +
    // cdk-overlay-container oscila a scrollbar do window momentaneamente).
    await this.selectionClipboard.copySubmissions(items);
  }

  // Click-outside-to-close: fecha o painel de filtros se o click for fora
  // dele E fora do botão de filtros.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.filterVisible) {
      return;
    }
    const target = event.target as HTMLElement;
    const host = this.elementRef.nativeElement as HTMLElement;
    const inPanel = host.querySelector('.separator')?.contains(target);
    const inToggle = host.querySelector('.chair-header-icon-button--filter')?.contains(target);
    if (inPanel || inToggle) {
      return;
    }
    this.filterVisible = false;
  }

  // Teclado no painel aberto: Esc fecha, ↑/↓ navegam entre chips.
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.filterVisible) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.filterVisible = false;
      const filterBtn = this.elementRef.nativeElement.querySelector('.chair-header-icon-button--filter') as HTMLElement;
      filterBtn?.focus();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const panel = this.elementRef.nativeElement.querySelector('.separator') as HTMLElement;
      if (!panel) {
        return;
      }
      const checkboxes = Array.from(
        panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
      );
      if (checkboxes.length === 0) {
        return;
      }
      const currentIndex = checkboxes.findIndex(cb => cb === document.activeElement);
      let nextIndex: number;
      if (event.key === 'ArrowDown') {
        nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % checkboxes.length;
      } else {
        nextIndex = currentIndex === -1
          ? checkboxes.length - 1
          : (currentIndex - 1 + checkboxes.length) % checkboxes.length;
      }
      event.preventDefault();
      checkboxes[nextIndex].focus();
    }
  }

  // Toggle do painel "Filtros". Ao abrir, foca no primeiro checkbox.
  toggleFilters(): void {
    this.filterVisible = !this.filterVisible;
    if (this.filterVisible) {
      setTimeout(() => {
        const firstCheckbox = this.elementRef.nativeElement.querySelector(
          '.separator input[type="checkbox"]'
        ) as HTMLInputElement;
        firstCheckbox?.focus();
      }, 50);
    }
  }

  // Smart toggle: se todos marcados, desmarca todos; senão marca todos.
  toggleAllFilters(event?: MouseEvent): void {
    event?.stopPropagation();
    const allChecked = this.allStatusesSelected;
    // `.filter-status-option` agora é um `<button>` toggle (olhinho).
    // Estado inclui/exclui vem da classe `.status-off` no próprio botão.
    const selector = allChecked
      ? '.submissions-list-filter .filter-status-option:not(.status-off)'
      : '.submissions-list-filter .filter-status-option.status-off';
    const buttons: NodeListOf<HTMLButtonElement> = this.elementRef.nativeElement.querySelectorAll(selector);
    buttons.forEach(b => b.click());
  }

  get allStatusesSelected(): boolean {
    return this.filter?.status?.length === this.submissionStatusFields.length;
  }

  public load(): void {
    setTimeout(() => {
      this.getSubmissions();
    });
  }

  get submissions(): Array<Submission> {
    return <Array<Submission>> this.elements;
  }

  getSubmissions(): void {
    this.navbarService.loadingStart();
    this.submissionsByEvent = null;
    const paginationOptions = NO_PAGINATION;
    paginationOptions.show = this.showAll;
    this.submissionService.getUserSubmissions(paginationOptions, this.filter, undefined, true).subscribe(pagination => {
      this.unfilteredElements = pagination.items as Submission[];
      this.filterPapers(<Submission[]>this.unfilteredElements);
      // Agrupa a partir do UNFILTERED — os grupos existem
      // independentemente dos chips de status. Visibilidade por card é
      // controlada por `submission.visible`, ajustada via
      // `updateSubmissionsByFilter()`. Sem isso, um refresh com todos
      // os chips desmarcados esvaziaria `submissionsByEvent` e um
      // chip subsequente não teria grupo pra revelar.
      this.getSubmissionsByEvent();
      this.updateSubmissionsByFilter();
      this.navbarService.loadingStop();
      window.scroll(0, 0);
      setTimeout(() => this.renderStatusCounts(), 150);
      // Um único equalize pós-load — o ResizeObserver instalado em
      // ngAfterViewInit captura mudanças tardias (app-file-upload monta
      // async). Substitui os 3 timeouts 200/600/1200 legados.
      setTimeout(() => this.equalizeCardColumns(), 200);
    });
  }

  // Agrupa submissões por evento (espelha getReviewsByEvent das revisões).
  // Dedup + agrupamento em UMA passada O(N) via Map. A versão anterior fazia
  // `findIndex` por elemento + `filter` por grupo (O(N²)) — sensível em
  // contas com muitas submissões.
  public getSubmissionsByEvent(): void {
    // Fonte = UNFILTERED. Grupos existem sempre que houve dado do
    // backend, mesmo se todos os chips estão desmarcados. A
    // filtragem por status vira só `submission.visible` no template.
    const subs: Array<Submission> = ((this.unfilteredElements as Submission[]) || []).filter(s => !!s.event);
    if (subs.length === 0) {
      this.submissionsByEvent = [];
      return;
    }
    const byEventId = new Map<number, SubmissionsByEvent>();
    for (const s of subs) {
      const evId = s.event.id;
      let group = byEventId.get(evId);
      if (!group) {
        // Constrói o holder diretamente (sem plainToClass) pra PRESERVAR as
        // instâncias reais de Submission — passá-las pelo class-transformer
        // as reserializaria em objetos planos, perdendo getters.
        group = new SubmissionsByEvent();
        group.event = s.event;
        group.submissions = [];
        byEventId.set(evId, group);
      }
      group.submissions.push(s);
    }
    byEventId.forEach(g => { g.count = g.submissions.length; });
    this.submissionsByEvent = Array.from(byEventId.values())
      .sort((a, b) => a.event.id - b.event.id);
  }

  ifVisibleSubmissions(submissions: Array<Submission>): boolean {
    return submissions.some(s => s.visible === true);
  }

  // Espelha `updateReviewsByFilter` do reviews: percorre a lista
  // agrupada e ajusta `submission.visible` conforme os status marcados
  // no menu de filtros. Chamado no `getFilterNotification` (toggle de
  // chip) — sem isso, o template renderiza tudo mesmo com chips
  // desmarcados, porque `submissionsByEvent` não é reagrupado.
  updateSubmissionsByFilter(): void {
    this.submissionsByEvent?.forEach(g => {
      g.submissions?.forEach(s => {
        s.visible = this.filter.status.includes(s.status);
      });
    });
    setTimeout(() => this.equalizeCardColumns(), 0);
  }

  public hasFilterResults(): boolean {
    // `.some` permite short-circuit (vs filter+length>0). Per CD essa
    // checagem roda; cada early-exit economiza um transform.
    return this.submissionsByEvent.some(sbe => this.hasFilterResultsEvent(sbe));
  }

  // Cache do último resultado do defaultFilter por (arrayRef, search).
  // Sem isso o método rodava por-tick durante CD, escaneando N submissões.
  private _filteredCache = new WeakMap<any[], { search: string; result: any[] }>();

  public hasFilterResultsEvent(submissionsByEvent): boolean {
    if (!this.search) {
      return (submissionsByEvent.submissions?.length ?? 0) > 0;
    }
    const subs = submissionsByEvent.submissions;
    if (!subs) { return false; }
    const cached = this._filteredCache.get(subs);
    if (cached && cached.search === this.search) {
      return cached.result.length > 0;
    }
    const result = this.defaultFilter.transform(
      subs,
      'title,event.acronym,event.year,event.name,authors',
      this.search
    );
    this._filteredCache.set(subs, { search: this.search, result });
    return result.length > 0;
  }

  // Conta submissões por status (ignora search/filtro atual). Usado pro
  // badge contador de cada chip no menu de status.
  private getStatusCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    this.submissionStatusFields.forEach(s => counts[String(s).toLowerCase()] = 0);
    const all = (this.unfilteredElements as Submission[]) || [];
    all.forEach(s => {
      const key = String(s.status || '').toLowerCase();
      if (counts[key] !== undefined) {
        counts[key]++;
      }
    });
    return counts;
  }

  // Contagem por status (chave = status.toLowerCase()). Passada ao
  // `<app-submissions-list-filter [statusCounts]>` que renderiza o
  // badge `.status-count` em cada linha via binding declarativo.
  public statusCounts: Record<string, number> = {};

  private renderStatusCounts(): void {
    this.statusCounts = this.getStatusCounts();
    // Ordem VISUAL das linhas de status ainda é setada por DOM (não
    // dá pra ligar `style.order` declarativamente sem custom directive).
    // Idempotente: só ajusta o `order` sem tocar em conteúdo.
    const rows: NodeListOf<HTMLElement> = this.elementRef.nativeElement.querySelectorAll(
      '.submissions-list-filter .filter-status-option'
    );
    rows.forEach(row => {
      const statusClass = (Array.from(row.classList).find(c => c.startsWith('status-') && c !== 'status-off') || '').replace('status-', '');
      if (!statusClass) return;
      row.style.order = String(SubmissionsComponent.STATUS_ORDER[statusClass] ?? 99);
    });
  }

  get checkedFilterNotification(): boolean {
    return this.submissionStatusFields.length > this.cachedStatusFields.length ? true : false;
  }

  get cachedStatusFields(): Array<string> {
    const cachedStatus = this.preferencesService.getPreference(LocalCache.STATUS_SUBMISSIONS);
    if (cachedStatus === undefined || cachedStatus === false) {
      // No cached value defined. Set all fields as checked.
      return this.submissionStatusFields;
    } else if (cachedStatus === '') {
      // Cached value is defined with no status marked.
      return [];
    }
    // Return array of string status that are marked.
    return (<string>cachedStatus).split(',');
  }

  get submissionStatusFields(): Array<string> {
    return SubmissionStatus.toIterable();
  }

  getFilterNotification(status: boolean) {
    this.filterNotification = status;
    // Preset: 1ª chamada = init do child, ignora. 2ª+ = ação do usuário
    // (chip toggle), encerra o preset e volta a salvar normalmente.
    if (this._presetActive) {
      if (this._presetInitConsumed) {
        this._presetActive = false;
      } else {
        this._presetInitConsumed = true;
      }
    }
    // Ajusta `submission.visible` conforme os chips marcados — mesmo
    // padrão do reviews.
    this.updateSubmissionsByFilter();
    // Adia pra microtask: os chip-checkboxes emitem este evento DURANTE o
    // ciclo de change-detection do init, então atualizar filterActiveCount
    // aqui sincronamente dispara ExpressionChangedAfterItHasBeenChecked.
    // Promise.then roda após o CD atual fechar.
    Promise.resolve().then(() => {
      this.recomputeFilterActiveCount();
      this.saveFiltersToStorage();
    });
  }

  private _cardsResizeObserver: ResizeObserver | null = null;
  private _equalizeRafHandle: number | null = null;

  ngAfterViewInit(): void {
    setTimeout(() => {
      // Preset ativo pula o restore — a intenção é uma view efêmera.
      if (!this._presetActive) {
        this.restoreFiltersFromStorage();
      }
      this.renderStatusCounts();
      this.recomputeFilterActiveCount();
      // Após mudanças assíncronas (restore + chips populando filter.status),
      // força uma checagem limpa pra estabilizar os bindings.
      this.cdr.detectChanges();
    }, 150);

    // ResizeObserver captura mudanças tardias em `.main-body` (app-file-
    // upload e afins montam async → colunas mudam depois do 1º equalize).
    // Substitui o triple-schedule 200/600/1200 do load.
    if (typeof ResizeObserver === 'undefined') { return; }
    const host: HTMLElement = this.elementRef?.nativeElement;
    const body = host?.querySelector('.main-body') as HTMLElement | null;
    if (!body) { return; }
    this._cardsResizeObserver = new ResizeObserver(() => {
      if (this._equalizeRafHandle) { return; }
      this._equalizeRafHandle = requestAnimationFrame(() => {
        this._equalizeRafHandle = null;
        this.equalizeCardColumns();
      });
    });
    this._cardsResizeObserver.observe(body);
  }

  ngOnDestroy(): void {
    if (this._cardsResizeObserver) { this._cardsResizeObserver.disconnect(); }
    if (this._equalizeRafHandle) { cancelAnimationFrame(this._equalizeRafHandle); }
  }

  private saveFiltersToStorage(): void {
    // Preset ativo → view efêmera, não escreve por cima do estado salvo.
    if (this._presetActive) { return; }
    try {
      if (this.filter?.status) {
        localStorage.setItem(
          SubmissionsComponent.FILTER_STORAGE_KEY,
          JSON.stringify(this.filter.status)
        );
      }
    } catch {
      // localStorage indisponível (modo privado / quota) — ignora.
    }
  }

  private restoreFiltersFromStorage(): void {
    let savedStatuses: string[] | null = null;
    try {
      const raw = localStorage.getItem(SubmissionsComponent.FILTER_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          savedStatuses = parsed;
        }
      }
    } catch {
      return;
    }
    if (!savedStatuses) {
      return;
    }
    const allStatuses = this.submissionStatusFields;
    const shouldUncheck = allStatuses.filter(s => !savedStatuses.includes(s));
    shouldUncheck.forEach(status => {
      const btn = this.elementRef.nativeElement.querySelector(
        `.submissions-list-filter .filter-status-option.status-${status.toLowerCase()}`
      ) as HTMLButtonElement | null;
      if (btn && !btn.classList.contains('status-off')) {
        btn.click();
      }
    });
  }

  public isFiltered(): boolean {
    return this.filter.status.length < this.submissionStatusFields.length;
  }

  // Nº de status excluídos do filtro (= filtros ativos). CACHEADO num campo
  // (não getter) — recalcular a cada change-detection causava
  // ExpressionChangedAfterItHasBeenChecked, pois os chip-checkboxes populam
  // filter.status DEPOIS do 1º render. Atualizado só em eventos (handlers).
  public filterActiveCount = 0;

  private recomputeFilterActiveCount(): void {
    if (!this.filter?.status) {
      this.filterActiveCount = 0;
      return;
    }
    const total = this.submissionStatusFields.length;
    const selected = this.filter.status.length;
    this.filterActiveCount = selected < total ? total - selected : 0;
  }
}
